import { useQuery } from '@apollo/client/react'
import {
  EmptyState,
  FailureNotice,
  LoadingState,
  PageLayout,
  buttonClasses,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { Link } from '@tanstack/react-router'

import { usePageTitle } from '../app/page-title.js'
import { useStartPractice } from '../features/learn/useStartPractice.js'
import {
  PracticeProgressList,
  type PracticeAction,
  type PracticeRun,
} from '../features/progress/PracticeProgress.js'
import { MyProgressDocument } from '../graphql/generated/operations.js'

/**
 * How a student is doing, one practice at a time.
 *
 * ── Not the tutor's chart ────────────────────────────────────────────────
 *
 * This screen used to be the tutor's analytical chart — a line per subject,
 * attempts or daily averages, a legend and a table behind it. That answers a
 * tutor's question, how a subject is going across everything in it, by
 * joining unrelated practices into one line. A student's question is simpler:
 * how did I do, and did it go better when I did it again? The only comparison
 * that answers that honestly is one practice against itself, so the practice
 * is the unit here, the subject is only where it is filed, and nothing is
 * averaged across practices. The tutor's chart is unchanged, on the tutor's
 * view of this student.
 *
 * ── What it offers, and what it does not ─────────────────────────────────
 *
 * Progress answers "how am I doing"; the home answers "what can I do now" and
 * the history "what have I done". So this is not a list of things to start —
 * but a practice the server says can be started again gets "Have another go",
 * through the home's own start flow, and one whose answers the server says
 * can be opened gets "See the answers". Nothing is recommended: which subject
 * to work on is not a question a handful of scores can answer.
 */
export function ProgressScreen() {
  const t = useTranslate()
  const language = useLanguage()
  usePageTitle(t('progress.title'))
  const { data, loading, error, refetch } = useQuery(MyProgressDocument)
  const { requestStart, startingId, feedback, dialog } = useStartPractice()

  if (loading && !data) {
    return (
      <PageLayout width="operational">
        <LoadingState className="p-0 py-8">{t('common.loading')}</LoadingState>
      </PageLayout>
    )
  }
  // Not an empty page: a student whose progress did not load has not been
  // told they have none.
  if (error && !data) {
    return (
      <PageLayout width="operational">
        <FailureNotice
          title={t('progress.loadError')}
          error={error}
          retrying={loading}
          onRetry={() => void refetch()}
        />
      </PageLayout>
    )
  }
  if (!data) return null

  const practices = data.myProgress.practices
  const assigned = new Map(data.myAssignedPractices.map((row) => [row.practiceId, row]))
  const reviewable = new Set(
    data.myHistory.filter((item) => item.canReview).map((item) => item.attemptId),
  )

  // Nothing finished yet: a calm first-run screen, with the way to something
  // that can be started when there is one.
  if (practices.length === 0) {
    const somethingToStart = data.myAssignedPractices.some((row) => row.canStart)
    return (
      <PageLayout width="operational">
        <EmptyState
          variant="page"
          title={t('progress.emptyTitle')}
          description={t('progress.emptyDescription')}
          {...(somethingToStart
            ? {
                action: (
                  <Link
                    to="/$language/learn"
                    params={{ language }}
                    className={buttonClasses({ variant: 'secondary', size: 'lg' })}
                  >
                    {t('progress.emptyAction')}
                  </Link>
                ),
              }
            : {})}
        />
      </PageLayout>
    )
  }

  /**
   * What one practice's summary may offer. Another go only when the server
   * says one can be started now and none is already open — an open one is the
   * home's "Continue practice", not a new go. Otherwise the answers, when the
   * server says the latest attempt's can be opened. Otherwise nothing: an
   * unavailable action is not shown as a dead button.
   */
  function actionFor(practice: PracticeRun): PracticeAction {
    const row = assigned.get(practice.practiceId)
    if (row?.canStart && row.latestAttempt?.status !== 'IN_PROGRESS') {
      return {
        kind: 'again',
        starting: startingId === practice.practiceId,
        onStart: () =>
          requestStart({
            practiceId: row.practiceId,
            title: row.title,
            timed: row.timed,
            durationMinutes: row.durationMinutes ?? null,
            inProgress: false,
          }),
      }
    }
    const latest = practice.attempts[0]
    if (latest && reviewable.has(latest.attemptId)) {
      return { kind: 'review', attemptId: latest.attemptId }
    }
    return { kind: 'none' }
  }

  return (
    <PageLayout width="operational">
      <p className="text-body text-text-secondary">{t('progress.intro')}</p>
      {/* A tutor's plan left some figures out. Said once, plainly, rather
          than as a gap the student has to puzzle over. */}
      {data.myProgress.progressWithheld ? (
        <p className="text-body-sm text-text-secondary">{t('progress.withheldNote')}</p>
      ) : null}
      {feedback}
      <PracticeProgressList practices={practices} actionFor={actionFor} />
      {dialog}
    </PageLayout>
  )
}
